import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchConfigFromAPI } from './config/api';
import { generateDefaultConfig, mergeConfigs } from './config/utils';
import { CONFIG_KEYS } from './config/constants';
import { readToken } from './utils/readToken';
import routeTemplates from './common/route-templates';
import queryString from 'query-string';

// Create the Config Context
const ConfigContext = createContext();

// Custom hook to use the config context
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    return {
      config: {},
      loading: false,
      error: null,
      refreshConfig: false,
    };
  }
  return context;
};

// Generate default config
let defaultConfig = generateDefaultConfig();

// Create a singleton config object that will be updated by the context
let currentConfig = { ...defaultConfig };

// Function to update the singleton config
const updateGlobalConfig = newConfig => {
  currentConfig = { ...newConfig };
};

// Config Provider Component
export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadConfig = async () => {
    try {
      // Check if token exists before making API call
      const token = readToken();
      if (!token) {
        // No token present, use default config and redirect to login
        updateGlobalConfig(defaultConfig);
        setConfig(defaultConfig);
        setLoading(false);

        // Redirect to login page if not already there, using the same logic as AuthenticatedRoute
        if (
          !window.location.pathname.includes('/login') &&
          !window.location.pathname.includes('/register') &&
          !window.location.pathname.includes('/passwordReset') && 
          !window.location.pathname.includes('/invite')
        ) {
          const searchParams = queryString.parse(window.location.search);
          const existingRedirectUrl = searchParams.redirectUrl;
          const redirectPath =
            routeTemplates.login +
            `?redirectUrl=${existingRedirectUrl ||
              encodeURIComponent(window.location.pathname + window.location.search)}`;
          window.location.href = redirectPath;
        }
        return;
      }
      setLoading(true);
      const apiConfig = await fetchConfigFromAPI();
      if (apiConfig) {
        const mergedConfig = mergeConfigs(defaultConfig, apiConfig);
        defaultConfig = mergedConfig; // Update defaultConfig with merged configuration
        updateGlobalConfig(mergedConfig); // Update the singleton config
        setConfig(mergedConfig);
      } else {
        updateGlobalConfig(defaultConfig); // Update the singleton config
        setConfig(defaultConfig);
      }
    } catch (err) {
      setError(err);
      updateGlobalConfig(defaultConfig); // Update the singleton config
      setConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const contextValue = useMemo(
    () => ({
      config,
      loading,
      error,
      refreshConfig: loadConfig,
    }),
    [config, loading, error]
  );

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

// Create an object to hold all config exports with dynamic getters
const configExports = {};

// Create dynamic getters for each config key
CONFIG_KEYS.forEach(key => {
  Object.defineProperty(configExports, key, {
    get() {
      // Handle special cases
      if (key === 'ZI_COOKIE') {
        return currentConfig.ZI_COOKIE || 'ziaccesstoken';
      }
      if (key === 'REACT_APP_DOZI_URL') {
        return currentConfig.REACT_APP_DOZI_URL;
      }
      if (key === 'DOZI_INBOX_URL') {
        const doziUrl = currentConfig.REACT_APP_DOZI_URL;
        if (!doziUrl) {
          console.warn('REACT_APP_DOZI_URL is not configured, DOZI_INBOX_URL will be undefined');
          return undefined;
        }
        return `${doziUrl}${
          window.location.pathname.includes('/mos') ? 'marketing-cloud/chat' : 'apps/chat'
        }`;
      }
      return currentConfig[key];
    },
    enumerable: true,
    configurable: true,
  });
});

// Export individual config values for backward compatibility
export const BE_ENDPOINT_NON_ZI = configExports.BE_ENDPOINT_NON_ZI;
export const BE_ENDPOINT_ZI = configExports.BE_ENDPOINT_ZI;
export const ZI_COOKIE = configExports.ZI_COOKIE;
export const DOZI_URL = configExports.REACT_APP_DOZI_URL;
export const DOZI_INBOX_URL = configExports.DOZI_INBOX_URL;
export const REACT_APP_SENTRY_DSN = configExports.REACT_APP_SENTRY_DSN;
export const REACT_APP_STRIPE_KEY = configExports.REACT_APP_STRIPE_KEY;
export const REACT_APP_REDIRECT_URI_SALESFORCE = configExports.REACT_APP_REDIRECT_URI_SALESFORCE;
export const REACT_APP_REDIRECT_URI_HUBSPOT = configExports.REACT_APP_REDIRECT_URI_HUBSPOT;
export const REACT_APP_PUSHER_TOKEN = configExports.REACT_APP_PUSHER_TOKEN;
export const REACT_APP_PUSHER_CLUSTER = configExports.REACT_APP_PUSHER_CLUSTER;
export const REACT_APP_CLIENT_ID_SALESFORCE = configExports.REACT_APP_CLIENT_ID_SALESFORCE;
export const REACT_APP_CLIENT_ID_HUBSPOT = configExports.REACT_APP_CLIENT_ID_HUBSPOT;
export const REACT_APP_MIXPANEL_TOKEN = configExports.REACT_APP_MIXPANEL_TOKEN;
export const NODE_ENV = configExports.NODE_ENV;
export const REACT_APP_SLACK_CLIENT_ID = configExports.REACT_APP_SLACK_CLIENT_ID;
export const REACT_APP_SLACK_OAUTH_URL = configExports.REACT_APP_SLACK_OAUTH_URL;
export const REACT_APP_ZENDESK_CLIENT_ID = configExports.REACT_APP_ZENDESK_CLIENT_ID;
export const REACT_APP_ZENDESK_OAUTH_URL = configExports.REACT_APP_ZENDESK_OAUTH_URL;
export const REACT_APP_TEAMS_CLIENT_ID = configExports.REACT_APP_TEAMS_CLIENT_ID;
export const REACT_APP_TEAMS_OAUTH_URL = configExports.REACT_APP_TEAMS_OAUTH_URL;
export const REACT_APP_REDIRECT_URI_TEAMS = configExports.REACT_APP_REDIRECT_URI_TEAMS;
export const REACT_APP_TEAMS_VALIDURLS = configExports.REACT_APP_TEAMS_VALIDURLS;
export const REACT_APP_ZENDESK_VALIDURLS = configExports.REACT_APP_ZENDESK_VALIDURLS;
export const REACT_APP_SUPPORT_PROJECT_KEY = configExports.REACT_APP_SUPPORT_PROJECT_KEY;
export const REACT_APP_SUPPORT_PROJECT_ID = configExports.REACT_APP_SUPPORT_PROJECT_ID;
export const REACT_APP_GOOGLE_CALENDAR_CLIENT_ID =
  configExports.REACT_APP_GOOGLE_CALENDAR_CLIENT_ID;
export const REACT_APP_GOOGLE_CALENDAR_OAUTH_URL =
  configExports.REACT_APP_GOOGLE_CALENDAR_OAUTH_URL;
export const REACT_APP_MICROSOFT_CALENDAR_CLIENT_ID =
  configExports.REACT_APP_MICROSOFT_CALENDAR_CLIENT_ID;
export const REACT_APP_MICROSOFT_CALENDAR_OAUTH_URL =
  configExports.REACT_APP_MICROSOFT_CALENDAR_OAUTH_URL;
export const REACT_APP_CS_DASHBOARD_VALID_URLS = configExports.REACT_APP_CS_DASHBOARD_VALID_URLS;
export const REACT_APP_ELOQUA_CLIENT_ID = configExports.REACT_APP_ELOQUA_CLIENT_ID;
export const REACT_APP_ELOQUA_OAUTH_URL = configExports.REACT_APP_ELOQUA_OAUTH_URL;
export const REACT_APP_ZOHO_OAUTH_URL = configExports.REACT_APP_ZOHO_OAUTH_URL;
export const REACT_APP_ZOHO_CLIENT_ID = configExports.REACT_APP_ZOHO_CLIENT_ID;
export const REACT_APP_HIDE_INTENT_RULE = configExports.REACT_APP_HIDE_INTENT_RULE;
export const REACT_APP_TRANSLOADIT_AUTH_KEY = configExports.REACT_APP_TRANSLOADIT_AUTH_KEY;
export const REACT_APP_TRANSLOADIT_TEMPLATE_ID = configExports.REACT_APP_TRANSLOADIT_TEMPLATE_ID;
export const REACT_APP_AMPLITUDE_API_KEY = configExports.REACT_APP_AMPLITUDE_API_KEY;
export const REACT_APP_DASHBOARD_IDS = configExports.REACT_APP_DASHBOARD_IDS;
export const REACT_APP_SISENSE_URL = configExports.REACT_APP_SISENSE_URL;
export const REACT_APP_SISENSE_AUTH_URL = configExports.REACT_APP_SISENSE_AUTH_URL;
export const REACT_APP_TRANSLOADIT_ACCOUNT_TEMPLATE_ID =
  configExports.REACT_APP_TRANSLOADIT_ACCOUNT_TEMPLATE_ID;
export const REACT_APP_GIPHY_KEY = configExports.REACT_APP_GIPHY_KEY;
export const REACT_APP_ZI_SCRIPTS_URL = configExports.REACT_APP_ZI_SCRIPTS_URL;

export default ConfigContext;
